import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SectionHero from '../../components/sections/SectionHero';
import SectionTrustBar from '../../components/sections/SectionTrustBar';
import SectionMidCTA from '../../components/sections/SectionMidCTA';
import SectionWhyChooseUs from '../../components/sections/SectionWhyChooseUs';
import SectionServiceAreas from '../../components/sections/SectionServiceAreas';
import SectionTestimonials from '../../components/sections/SectionTestimonials';
import SectionFAQ from '../../components/sections/SectionFAQ';
import SectionFinalCTA from '../../components/sections/SectionFinalCTA';
import PracticeAreaSection from './components/PracticeAreaSection';

const practiceAreas = [
  { id: 'dui-defense', icon: 'ri-car-line', label: 'DUI Defense' },
  { id: 'drug-charges', icon: 'ri-capsule-line', label: 'Drug Charges' },
  { id: 'domestic-violence', icon: 'ri-home-heart-line', label: 'Domestic Violence' },
  { id: 'assault', icon: 'ri-sword-line', label: 'Assault & Violent Crimes' },
  { id: 'theft', icon: 'ri-safe-2-line', label: 'Theft & Property Crimes' },
  { id: 'white-collar', icon: 'ri-bank-line', label: 'White Collar Crimes' },
];

const duiPoints = [
  { icon: 'ri-search-eye-line', title: 'Challenge the Traffic Stop', desc: 'We scrutinize the legality of every stop. Unlawful stops can get the entire case dismissed.' },
  { icon: 'ri-test-tube-line', title: 'Attack Breathalyzer Evidence', desc: 'Calibration records, administration errors, and medical conditions all affect BAC results.' },
  { icon: 'ri-key-2-line', title: 'License Suspension Defense', desc: 'We fight administrative and criminal proceedings simultaneously to protect your driving privileges.' },
  { icon: 'ri-repeat-line', title: 'Repeat Offense Strategy', desc: 'Prior DWI convictions raise stakes dramatically — our defense adapts to protect you from enhanced penalties.' },
];

const drugPoints = [
  { icon: 'ri-search-2-line', title: 'Suppress Unlawful Searches', desc: 'Evidence from illegal searches or seizures can be suppressed and charges dismissed.' },
  { icon: 'ri-file-list-3-line', title: 'Challenge Possession Intent', desc: 'Proving intent to distribute vs. simple possession is a critical distinction we challenge aggressively.' },
  { icon: 'ri-shield-user-line', title: 'Diversion Programs', desc: 'First-time offenders may qualify for programs that avoid a permanent criminal record entirely.' },
  { icon: 'ri-microscope-line', title: 'Lab Evidence Review', desc: 'Chain of custody failures and lab errors can invalidate drug evidence and lead to dismissal.' },
];

const dvPoints = [
  { icon: 'ri-question-answer-line', title: 'False Allegation Defense', desc: 'Domestic charges are frequently driven by disputes. We challenge inconsistent and fabricated allegations.' },
  { icon: 'ri-file-forbid-line', title: 'Protective Order Challenges', desc: 'Emergency protective orders can be overreaching. We respond immediately to protect your rights and access.' },
  { icon: 'ri-home-2-line', title: 'Family Law Coordination', desc: 'Domestic violence charges intersect with divorce and custody. We protect you across both proceedings.' },
  { icon: 'ri-user-star-line', title: 'Witness Credibility Analysis', desc: 'Witness statements and bias are rigorously examined to expose inconsistencies in the prosecution\'s case.' },
];

const assaultPoints = [
  { icon: 'ri-shield-check-line', title: 'Self-Defense Claims', desc: 'We build strong affirmative self-defense arguments backed by evidence and witness testimony.' },
  { icon: 'ri-eye-off-line', title: 'Eyewitness Challenges', desc: 'Eyewitness misidentification is a leading cause of wrongful conviction — we dissect it thoroughly.' },
  { icon: 'ri-award-line', title: 'Aggravated vs. Simple Assault', desc: 'Charge severity determines consequences. We fight to reduce felony assault charges wherever possible.' },
  { icon: 'ri-timer-line', title: 'Immediate Crisis Response', desc: 'Violent crime arrests demand instant legal action. We respond same-day to protect your rights from hour one.' },
];

const theftPoints = [
  { icon: 'ri-mind-map', title: 'Intent Element Defense', desc: 'Theft requires criminal intent. We challenge the prosecution\'s ability to prove you intended to permanently deprive.' },
  { icon: 'ri-store-2-line', title: 'Shoplifting & Misdemeanor Defense', desc: 'Even minor theft charges carry lasting record consequences. We fight to keep your record clean.' },
  { icon: 'ri-building-4-line', title: 'Burglary & Robbery Defense', desc: 'Felony property crime allegations carry severe penalties requiring aggressive, experienced representation.' },
  { icon: 'ri-exchange-funds-line', title: 'Restitution Negotiation', desc: 'Where appropriate, restitution agreements can resolve charges without trial or permanent record impact.' },
];

const whiteCollarPoints = [
  { icon: 'ri-line-chart-line', title: 'Financial Evidence Analysis', desc: 'We retain forensic accountants and experts to challenge complex financial evidence used against you.' },
  { icon: 'ri-mail-forbid-line', title: 'Wire & Mail Fraud Defense', desc: 'Federal fraud charges require specialized knowledge of federal procedure and sentencing guidelines.' },
  { icon: 'ri-building-line', title: 'Embezzlement Defense', desc: 'Embezzlement prosecutions often hinge on intent and accounting disputes — areas where we excel.' },
  { icon: 'ri-team-line', title: 'Corporate & Executive Defense', desc: 'We defend business owners and executives facing regulatory investigations and white collar prosecutions.' },
];

const reasons = [
  { icon: 'ri-focus-3-line', title: 'Multi-Area Criminal Defense', desc: 'From DUI to white collar crime — Dave Bunger handles the full spectrum of criminal charges with equal expertise.' },
  { icon: 'ri-sword-line', title: 'Aggressive Defense Strategy', desc: 'Every case receives a custom-built defense strategy. We challenge every element of the prosecution\'s case.' },
  { icon: 'ri-user-heart-line', title: 'Direct Attorney Access', desc: 'You speak directly with Dave Bunger throughout your case — not a paralegal or case manager.' },
  { icon: 'ri-time-line', title: 'Same-Day Response', desc: 'Criminal charges are time-sensitive. We respond immediately to protect your rights from the first hour.' },
  { icon: 'ri-map-pin-2-line', title: 'Deep Local Court Knowledge', desc: 'Extensive experience across Washington County courts gives our clients a strategic edge over out-of-area attorneys.' },
];

const areas = [
  { name: 'Brenham', county: 'Washington County', icon: 'ri-building-2-line' },
  { name: 'College Station', county: 'Brazos County', icon: 'ri-community-line' },
  { name: 'Bellville', county: 'Austin County', icon: 'ri-map-pin-line' },
  { name: 'Caldwell', county: 'Burleson County', icon: 'ri-map-pin-line' },
  { name: 'Navasota', county: 'Grimes County', icon: 'ri-map-pin-line' },
  { name: 'Burton', county: 'Washington County', icon: 'ri-map-pin-line' },
];

const faqs = [
  { q: 'What criminal charges does The Bunger Law Firm handle?', a: 'We handle the full spectrum of criminal charges including DUI/DWI, drug possession and distribution, domestic violence, assault and violent crimes, theft and property crimes, and white collar offenses. Every practice area receives the same level of aggressive, strategic representation.' },
  { q: 'Can a DUI charge be dismissed in Texas?', a: 'Yes. DUI charges can be dismissed when there are issues with the traffic stop\'s legality, breathalyzer calibration errors, improper field sobriety testing, or constitutional violations during arrest. We examine every angle to pursue dismissal or significant reduction.' },
  { q: 'What should I do immediately after a drug arrest?', a: 'Remain completely silent and contact an attorney immediately. Do not consent to searches, answer questions, or make statements to law enforcement without legal counsel present. Early attorney involvement is the single most impactful step you can take.' },
  { q: 'Can domestic violence charges be dropped if the alleged victim recants?', a: 'Not automatically. In Texas, the state can proceed with prosecution even if the alleged victim does not want to press charges. This is a critical reason why experienced legal representation is essential from the very beginning of a domestic violence case.' },
  { q: 'What is the difference between assault and aggravated assault in Texas?', a: 'Simple assault generally involves intentionally causing bodily injury or threatening imminent harm. Aggravated assault involves serious bodily injury or use of a deadly weapon and is charged as a felony. The distinction dramatically affects potential penalties and defense strategy.' },
  { q: 'Are white collar crimes tried in federal or state court?', a: 'It depends on the specific charges. Mail fraud, wire fraud, and federal financial crimes are prosecuted in federal court. State-level fraud, theft, and embezzlement proceed in state court. We are experienced in both venues and handle cases across all levels.' },
];

export default function PracticeAreasPage() {
  useEffect(() => {
    document.title = "Practice Areas | Criminal Defense Attorney Brenham TX | Bunger Law Firm";
  }, []);

  return (
    <div className="font-sans">
      <Navbar />
      <main>
        {/* Hero */}
        <SectionHero
          title="Criminal Defense Practice"
          titleHighlight="Areas"
          subtitle="From DUI defense to white collar crime, The Bunger Law Firm provides experienced, aggressive representation across the full spectrum of criminal charges in Texas."
          bgImage="https://public.readdy.ai/ai/img_res/167877f4f3285afeb02dd6f72210b040.jpg"
          badge="Practice Areas"
        />

        {/* Trust Bar */}
        <SectionTrustBar />

        {/* Jump nav */}
        <section className="py-10 bg-[#f8f5ef]">
          <div className="max-w-7xl mx-auto px-6">
            <p className="text-center text-[#3a4a5c] text-sm font-medium mb-6 tracking-wide uppercase">Jump to a Practice Area</p>
            <div className="flex flex-wrap justify-center gap-3">
              {practiceAreas.map((area) => (
                <a
                  key={area.id}
                  href={`#${area.id}`}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#c9a84c]/30 bg-white text-[#070c14] text-sm font-semibold hover:bg-[#c9a84c] hover:text-[#070c14] hover:border-[#c9a84c] transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${area.icon} text-[#c9a84c] text-xs`} />
                  </div>
                  {area.label}
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="w-full h-px bg-[#e8e0d0]" />

        {/* DUI Defense */}
        <PracticeAreaSection
          id="dui-defense"
          icon="ri-car-line"
          title="DUI & DWI Defense"
          subtitle="DUI Defense"
          description="A DUI arrest doesn't have to mean a conviction. We challenge the legality of the traffic stop, the accuracy of breathalyzer and field sobriety tests, and every procedural step of your arrest. From first-time offenders to repeat DWI charges and DUI with injury cases, we provide the aggressive, knowledgeable defense you need to protect your license, your record, and your future. Time is critical — license suspension hearings have strict deadlines, and we fight both tracks simultaneously."
          points={duiPoints}
          image="https://readdy.ai/api/search-image?query=cinematic%20nighttime%20Texas%20highway%20intersection%20with%20distant%20traffic%20lights%20rain%20wet%20asphalt%20dark%20moody%20atmosphere%20low%20angle%20perspective%20no%20cars%20no%20people%20dramatic%20road%20scene&width=800&height=500&seq=dui_area_2026&orientation=landscape"
        />

        <div className="w-full h-px bg-[#e8e0d0] max-w-7xl mx-auto" />

        {/* Drug Charges */}
        <PracticeAreaSection
          id="drug-charges"
          icon="ri-capsule-line"
          title="Drug Charges Defense"
          subtitle="Drug Charges"
          description="Drug charges in Texas range from simple possession misdemeanors to felony distribution and manufacturing charges carrying decades in prison. We analyze the circumstances of every arrest — from the legality of the search to how evidence was handled — to find every available defense. We fight to suppress illegally obtained evidence, challenge intent, and where possible, pursue diversion programs that keep your record clean and your future intact."
          points={drugPoints}
          reversed
          image="https://readdy.ai/api/search-image?query=serious%20professional%20attorney%20office%20desk%20with%20legal%20documents%20law%20books%20gavel%20dark%20polished%20wood%20sophisticated%20lighting%20no%20people%20courtroom%20preparation%20workspace&width=800&height=500&seq=drug_area_2026&orientation=landscape"
        />

        <div className="w-full h-px bg-[#e8e0d0] max-w-7xl mx-auto" />

        {/* Domestic Violence */}
        <PracticeAreaSection
          id="domestic-violence"
          icon="ri-home-heart-line"
          title="Domestic Violence Defense"
          subtitle="Domestic Violence"
          description="Domestic violence charges can shatter careers, custody arrangements, and reputations — sometimes based on false or exaggerated allegations. Texas allows prosecution to proceed even if the alleged victim does not cooperate, making skilled legal representation essential from day one. We move immediately to address protective orders, scrutinize the evidence and witness statements, and build a comprehensive defense strategy designed to protect your freedom and your family relationships."
          points={dvPoints}
          image="https://readdy.ai/api/search-image?query=solemn%20dignified%20courtroom%20interior%20with%20wooden%20benches%20judge%20podium%20american%20flag%20warm%20dramatic%20lighting%20empty%20no%20people%20serious%20legal%20atmosphere&width=800&height=500&seq=dv_area_2026&orientation=landscape"
        />

        <div className="w-full h-px bg-[#e8e0d0] max-w-7xl mx-auto" />

        {/* Assault & Violent Crimes */}
        <PracticeAreaSection
          id="assault"
          icon="ri-sword-line"
          title="Assault & Violent Crimes"
          subtitle="Assault & Violent Crimes"
          description="Assault and violent crime accusations trigger immediate, aggressive law enforcement action — and require the same level of immediate, aggressive legal response. Whether you're facing simple assault, aggravated assault, or more serious violent crime charges, we challenge the evidence, the witnesses, and the prosecution's ability to prove every element. We build self-defense cases, attack eyewitness identification, and work to reduce felony charges wherever legally possible."
          points={assaultPoints}
          reversed
          image="https://readdy.ai/api/search-image?query=empty%20dramatic%20Texas%20courtroom%20with%20wood%20paneling%20high%20ceilings%20american%20flag%20elevated%20judge%20bench%20serious%20atmosphere%20no%20people%20law%20justice&width=800&height=500&seq=assault_area_2026&orientation=landscape"
        />

        <div className="w-full h-px bg-[#e8e0d0] max-w-7xl mx-auto" />

        {/* Theft & Property Crimes */}
        <PracticeAreaSection
          id="theft"
          icon="ri-safe-2-line"
          title="Theft & Property Crimes"
          subtitle="Theft & Property Crimes"
          description="Property crime charges — from shoplifting to felony burglary and robbery — carry serious consequences including incarceration, fines, and a permanent criminal record that follows you for life. The key battleground is criminal intent: we challenge whether you truly intended to permanently deprive the owner of their property. We handle misdemeanor theft, felony theft, burglary, and robbery charges with the same relentless focus on minimizing consequences and protecting your future."
          points={theftPoints}
          image="https://readdy.ai/api/search-image?query=refined%20legal%20office%20conference%20room%20polished%20mahogany%20table%20leather%20chairs%20diplomas%20framed%20on%20walls%20professional%20neutral%20lighting%20sophisticated%20no%20people&width=800&height=500&seq=theft_area_2026&orientation=landscape"
        />

        <div className="w-full h-px bg-[#e8e0d0] max-w-7xl mx-auto" />

        {/* White Collar Crimes */}
        <PracticeAreaSection
          id="white-collar"
          icon="ri-bank-line"
          title="White Collar Crime Defense"
          subtitle="White Collar Crimes"
          description="White collar crime charges — fraud, embezzlement, forgery, money laundering, and financial crimes — carry unique complexity requiring specialized legal and financial expertise. These cases often involve extensive documentation, expert witnesses, and sophisticated prosecutorial strategies. We work with forensic accountants and financial experts to challenge the evidence, expose weaknesses in the government's theory, and provide powerful defense for business owners and executives facing financial crime allegations."
          points={whiteCollarPoints}
          reversed
          image="https://readdy.ai/api/search-image?query=elegant%20upscale%20law%20firm%20office%20city%20view%20floor%20to%20ceiling%20windows%20mahogany%20furniture%20bookshelves%20golden%20sunset%20light%20sophisticated%20professional%20no%20people&width=800&height=500&seq=wc_area_2026&orientation=landscape"
        />

        {/* Mid CTA */}
        <SectionMidCTA
          variant="dark"
          title="Facing Any of These Charges? Call Us Now."
          subtitle="Free, confidential consultation. Same-day response available. We protect your rights from the first call."
        />

        {/* Why Choose Us */}
        <SectionWhyChooseUs
          reasons={reasons}
          title="Why Choose Bunger Law For Your Defense"
          bodyText="Whatever criminal charge you face, Dave Bunger brings deep expertise, aggressive strategy, and direct personal attention to your case. One attorney. Full commitment. Real results across all of Washington County and beyond."
        />

        {/* Service Areas */}
        <SectionServiceAreas
          areas={areas}
          title="Criminal Defense Across Texas"
          subtitle="We represent clients charged across Washington, Brazos, Austin, Burleson, and Grimes Counties with local knowledge that delivers a real strategic edge."
          localText="Searching for a criminal defense attorney in Brenham or Washington County? The Bunger Law Firm provides immediate, aggressive criminal defense representation tailored to local courts, local prosecutors, and local procedure."
        />

        {/* Mid CTA 2 */}
        <SectionMidCTA
          variant="gold"
          title="Your Defense Starts With One Call"
          subtitle="Free consultations. Direct attorney access. No obligation. Call (979) 530-0250 today."
        />

        {/* Testimonials */}
        <SectionTestimonials />

        {/* FAQ */}
        <SectionFAQ faqs={faqs} title="Practice Areas — FAQs" />

        {/* Final CTA */}
        <SectionFinalCTA />
      </main>
      <Footer />
    </div>
  );
}
